import { useCallback, useMemo, useState } from 'react';
import { normalizePostBody } from '../utils/postBody';

const INITIAL_STATE = {
    isOpen: false,
    isProcessing: false,
    error: null,
    targetPost: null,
};

const defaultBuildDeleteMessage = (post) => {
    if (!post) {
        return "Are you sure you want to delete this post? This action cannot be undone.";
    }

    const source = normalizePostBody(post?.body);
    const cleaned = source.replace(/\s+/g, ' ').trim();
    if (!cleaned) {
        return "Are you sure you want to delete this post? This action cannot be undone.";
    }

    const snippet = cleaned.slice(0, 120);
    const displaySnippet = snippet ? `“${snippet}${cleaned.length > 120 ? '…' : ''}”` : '';
    const base = "Are you sure you want to delete this post?";
    const guidance = "This action cannot be undone.";
    return displaySnippet ? `${base} ${displaySnippet} ${guidance}` : `${base} ${guidance}`;
};

export const usePostDeleteDialog = (posts, deleteHandler, options = {}) => {
    const { buildMessage = defaultBuildDeleteMessage } = options;
    const [state, setState] = useState(INITIAL_STATE);

    const resolvePost = useCallback((postOrId) => {
        if (postOrId && typeof postOrId === 'object' && postOrId.id != null) {
            return postOrId;
        }

        const targetId = postOrId != null ? String(postOrId) : null;
        if (!targetId) {
            return null;
        }

        if (!Array.isArray(posts)) {
            return { id: postOrId };
        }

        return posts.find((item) => item && String(item.id) === targetId) || { id: postOrId };
    }, [posts]);

    const requestDelete = useCallback((postOrId) => {
        setState({
            isOpen: true,
            isProcessing: false,
            error: null,
            targetPost: resolvePost(postOrId),
        });
    }, [resolvePost]);

    const cancelDelete = useCallback(() => {
        setState((prev) => (prev.isProcessing ? prev : INITIAL_STATE));
    }, []);

    const confirmDelete = useCallback(async () => {
        let targetId = null;

        setState((prev) => {
            targetId = prev.targetPost?.id ?? null;
            if (!targetId) {
                return INITIAL_STATE;
            }
            return {
                ...prev,
                isProcessing: true,
                error: null,
            };
        });

        if (!targetId || typeof deleteHandler !== 'function') {
            setState(INITIAL_STATE);
            return;
        }

        try {
            await deleteHandler(targetId);
            setState(INITIAL_STATE);
        } catch (error) {
            setState((prev) => ({
                ...prev,
                isProcessing: false,
                error: error?.response?.data?.message || error.message || 'Failed to delete post',
            }));
        }
    }, [deleteHandler]);

    const message = useMemo(() => buildMessage(state.targetPost), [state.targetPost, buildMessage]);

    return {
        state,
        requestDelete,
        cancelDelete,
        confirmDelete,
        message,
    };
};

export default usePostDeleteDialog;
